import { format } from 'date-fns';
import { ConversationWithUser } from '@/types/messages';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

export interface ConversationListProps {
  conversations: ConversationWithUser[];
  currentConversation: string | null;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onReportConversation: (id: string, reportedUserId: string) => void;
  isLoading: boolean;
  unreadMessages: Record<string, number>;
}

function formatMessageTime(timestamp: number | null | undefined): string {
  if (!timestamp) return '';
  try {
    return format(new Date(timestamp), 'MMM d');
  } catch (error) {
    console.error('Invalid timestamp:', timestamp);
    return '';
  }
}

export function ConversationList({
  conversations,
  currentConversation,
  onSelectConversation,
  onDeleteConversation,
  onReportConversation,
  isLoading,
}: Omit<ConversationListProps, 'unreadMessages'>) {
  const { unreadCounts, clearUnreadCount } = useUnreadMessages();

  const handleConversationSelect = async (conversationId: string) => {
    await clearUnreadCount(conversationId);
    onSelectConversation(conversationId);
  };

  if (isLoading) {
    return <div className="p-4 text-gray-100">Loading conversations...</div>;
  }

  const handleAction = (
    e: React.MouseEvent,
    action: () => void
  ) => {
    e.stopPropagation();
    action();
  };

  return (
    <>
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-gray-100">No conversations yet</p>
              <p className='text-sm mt-2 text-gray-400'>Your conversations will appear here</p>
            </div>
          ) : (
            conversations.map(conversation => (
              <div
                key={conversation.id}
                className={`p-4 border-b border-gray-800 cursor-pointer hover:bg-gray-800 ${
                  currentConversation === conversation.id ? 'bg-gray-800' : ''
                }`}
                onClick={() => handleConversationSelect(conversation.id)}
              >
                <div className="flex items-center group">
                  <div className="relative">
                    {conversation.otherUser.imageUrl && (
                      <img
                        src={conversation.otherUser.imageUrl}
                        alt={conversation.otherUser.name}
                        className="w-10 h-10 rounded-full mr-3"
                      />
                    )}
                    {unreadCounts[conversation.id] > 0 && (
                      <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCounts[conversation.id]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-100 truncate">
                      {conversation.otherUser.name}
                    </div>
                    {conversation.lastMessage && (
                      <div className={`text-sm truncate ${
                        unreadCounts[conversation.id] > 0 
                          ? 'text-gray-100 font-medium' 
                          : 'text-gray-300'
                      }`}>
                        {conversation.lastMessage.content}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center ml-2">
                    {conversation.lastMessage && conversation.lastMessage.timestamp && (
                      <div className="text-xs text-gray-400 mr-2">
                        {formatMessageTime(conversation.lastMessage.timestamp)}
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button 
                          className="p-1 rounded-full hover:bg-gray-700"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <MoreVertical className="h-4 w-4 text-gray-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        align="end" 
                        className="w-48"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={(e) => handleAction(e, () => onDeleteConversation(conversation.id))}
                          className="text-red-500"
                        >
                          Delete Conversation
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleAction(e, () => onReportConversation(
                            conversation.id,
                            conversation.otherUser.id
                          ))}
                        >
                          Report Conversation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}